/**
 * Active volcanoes and seismic activity for the map layer.
 * Uses USGS public feeds — for awareness only, not a safety system.
 */

const USGS_WEEK =
  'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/4.5_week.geojson';

export interface SeismicMarker {
  lat: number;
  lng: number;
  mag: number;
  depthKm: number | null;
  timeMs: number | null;
  place: string;
  url: string;
}

interface UsgsFeature {
  geometry?: { coordinates?: [number, number, number] };
  properties?: {
    mag?: number;
    time?: number;
    place?: string;
    title?: string;
    url?: string;
  };
}

export async function fetchSeismicMarkers(options?: {
  signal?: AbortSignal;
}): Promise<SeismicMarker[]> {
  try {
    const res = await fetch(USGS_WEEK, { signal: options?.signal });
    if (!res.ok) throw new Error(`USGS ${res.status}`);
    const data = (await res.json()) as { features?: UsgsFeature[] };
    const features = data.features ?? [];
    const out: SeismicMarker[] = [];
    for (const f of features) {
      const coords = f.geometry?.coordinates;
      if (!coords || coords.length < 2) continue;
      const lng = coords[0];
      const lat = coords[1];
      if (typeof lat !== 'number' || typeof lng !== 'number') continue;
      const mag = f.properties?.mag;
      const depthRaw = coords[2];
      const depthKm =
        typeof depthRaw === 'number' && Number.isFinite(depthRaw)
          ? depthRaw
          : null;
      const timeRaw = f.properties?.time;
      const timeMs =
        typeof timeRaw === 'number' && Number.isFinite(timeRaw)
          ? timeRaw
          : null;
      const place = f.properties?.place ?? f.properties?.title ?? 'Earthquake';
      const url =
        f.properties?.url ??
        `https://earthquake.usgs.gov/earthquakes/eventpage/${(f as { id?: string }).id ?? ''}`;
      out.push({
        lat,
        lng,
        mag: typeof mag === 'number' && !Number.isNaN(mag) ? mag : 0,
        depthKm,
        timeMs,
        place,
        url,
      });
    }
    return out;
  } catch {
    return [];
  }
}

export interface VolcanoMarker {
  lat: number;
  lng: number;
  name: string;
  region: string;
}

/** Curated list of frequently active or monitored volcanoes (non-exhaustive). */
export const ACTIVE_VOLCANOES: VolcanoMarker[] = [
  { lat: 19.421, lng: -155.287, name: 'Kīlauea', region: 'Hawaiʻi' },
  { lat: 19.472, lng: -155.592, name: 'Mauna Loa', region: 'Hawaiʻi' },
  { lat: 32.741, lng: -16.972, name: 'Pico do Fogo', region: 'Cape Verde' },
  { lat: 40.819, lng: 14.426, name: 'Vesuvius', region: 'Italy' },
  { lat: 37.693, lng: 15.004, name: 'Etna', region: 'Italy' },
  { lat: 36.057, lng: 140.111, name: 'Sakurajima', region: 'Japan' },
  { lat: 35.36, lng: 138.73, name: 'Fuji', region: 'Japan' },
  { lat: -7.54, lng: 110.445, name: 'Merapi', region: 'Indonesia' },
  { lat: -8.108, lng: 115.092, name: 'Agung', region: 'Indonesia' },
  { lat: -6.102, lng: 106.108, name: 'Krakatau', region: 'Indonesia' },
  { lat: 14.002, lng: 120.993, name: 'Taal', region: 'Philippines' },
  { lat: 13.257, lng: 123.685, name: 'Mayon', region: 'Philippines' },
  { lat: 15.13, lng: 120.35, name: 'Pinatubo', region: 'Philippines' },
  { lat: 19.528, lng: -103.619, name: 'Colima', region: 'Mexico' },
  { lat: 19.023, lng: -98.622, name: 'Popocatépetl', region: 'Mexico' },
  { lat: 14.474, lng: -90.88, name: 'Fuego', region: 'Guatemala' },
  { lat: 4.895, lng: -75.318, name: 'Nevado del Ruiz', region: 'Colombia' },
  { lat: -0.677, lng: -78.436, name: 'Cotopaxi', region: 'Ecuador' },
  { lat: -15.78, lng: -72.115, name: 'Sabancaya', region: 'Peru' },
  { lat: -41.12, lng: -71.3, name: 'Calbuco', region: 'Chile' },
  { lat: -43.378, lng: 172.117, name: 'White Island', region: 'New Zealand' },
  { lat: -37.52, lng: 177.18, name: 'Whakaari', region: 'New Zealand' },
  { lat: 63.63, lng: -19.62, name: 'Eyjafjallajökull', region: 'Iceland' },
  { lat: 64.42, lng: -17.33, name: 'Grímsvötn', region: 'Iceland' },
  { lat: 65.03, lng: -16.75, name: 'Bárðarbunga', region: 'Iceland' },
  { lat: 55.97, lng: 160.6, name: 'Klyuchevskoy', region: 'Russia' },
  { lat: 51.885, lng: 157.53, name: 'Bezymianny', region: 'Russia' },
  { lat: 46.2, lng: 122.19, name: 'St. Helens', region: 'US' },
  { lat: 45.374, lng: -121.695, name: 'Mount Hood', region: 'US' },
  { lat: 38.292, lng: -122.457, name: 'Clear Lake Volcanic Field', region: 'US' },
  { lat: 60.485, lng: -152.742, name: 'Augustine', region: 'US (Alaska)' },
  { lat: 58.27, lng: -155.16, name: 'Novarupta', region: 'US (Alaska)' },
  { lat: 54.05, lng: -166.93, name: 'Cleveland', region: 'US (Alaska)' },
  { lat: -2.99, lng: 29.22, name: 'Nyiragongo', region: 'DRC' },
  { lat: 1.7, lng: 37.85, name: 'Ol Doinyo Lengai', region: 'Tanzania' },
];
